/**
 * todayFeedApi — participant client for the Today Timeline v2 engagement feed
 * (backend: GET/POST /api/portal/runtime/today, flag-gated on TODAY_FEED_V2_ENABLED).
 * Follows the runtimeApi thunk convention; uses the participant portalApi client.
 */
import portalApi from '../../../utils/portalApi';

export interface TodayFeedItem {
  position: number;
  kind: 'anchored' | 'ambient';
  ref: string;
  surface: string;
  type: string;
  render_band: string;
  card_id: string | null;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  image: string | null;
  video: { url: string; presenter: string | null; poster: string | null; title?: string | null } | null;
  blog: { url: string; title?: string | null; excerpt?: string | null; thumbnail?: string | null } | null;
  content: { title?: string; summary?: string; body_html?: string; questions?: string[]; reflection?: string } | null;
  week: number | null;
  estimated_time: number | null;
  status: string | null;
  points?: { learning?: number; builder?: number; community?: number } | null;
  interacted: boolean;
  author?: { name: string; avatar_url: string | null; level: number } | null;
  // Community-post items (`ref` = `community:<postId>`) have NO card_id — they
  // are a post, not a curriculum card. These carry the post's own identity and
  // its server-resolved ritual label so the client opens the post's discussion
  // thread rather than falling back to the ref as a card id.
  community_post_id?: string | null;
  student_label?: string | null;
  like_count?: number | null;
  comment_count?: number | null;
  // Project-task items: their real destination is the project workspace, not a
  // card drawer. Both ids are needed for /portal/projects/workspace/:p/:t.
  project_id?: string | null;
  project_task_id?: string | null;
  /** Project items: the button verb for a task that is not built ("Submit", "Demo Day"). */
  cta_verb?: string | null;
}

export interface TodayPage {
  items: TodayFeedItem[];
  nextCursor: number;
  exhausted: boolean;
  /** C107: an Explorer's pre-first-win starter lineup (five items, no further pages). Absent on older servers. */
  starter?: boolean;
}

export type TodayInteraction = 'open' | 'click' | 'complete' | 'dismiss';

/** C107: a card that scrolled into view ('seen') or was opened, and which door: the feed or the banner. */
export interface TodayCardEvent { ref: string; event: 'seen' | 'open'; source: 'feed' | 'banner' }
/** Matches the server's per-call bound (todayCardEvents.MAX_EVENTS_PER_CALL). */
export const MAX_EVENTS_PER_CALL = 30;

/** C107: a card opened from the Today banner, under the same `card:<id>` ref the feed uses. Fire and forget. */
export function recordBannerOpen(cardId: string): void {
  void todayFeedApi.events([{ ref: `card:${cardId}`, event: 'open', source: 'banner' }]).catch(() => { /* measurement only */ });
}

export const todayFeedApi = {
  // `seed` reshuffles the lineup per visit (stable within a visit). Generate one
  // fresh at feed mount and pass the SAME value for every page in that session.
  list: (cursor = 0, limit = 10, seed?: number): Promise<TodayPage> =>
    portalApi
      .get('/api/portal/runtime/today', { params: { cursor, limit, ...(seed != null ? { seed } : {}) }, timeout: 15000 })
      .then((r) => r.data as TodayPage),
  interact: (cardRef: string, action: TodayInteraction): Promise<{ ok: true }> =>
    portalApi
      .post(`/api/portal/runtime/today/${encodeURIComponent(cardRef)}/interact`, { action })
      .then((r) => r.data),
  /** Measurement only: callers ignore failures. */
  events: (events: TodayCardEvent[]): Promise<{ ok: true; recorded: number }> =>
    portalApi
      .post('/api/portal/runtime/today/events', { events: events.slice(0, MAX_EVENTS_PER_CALL) }, { timeout: 10000 })
      .then((r) => r.data),
};
